import { types, applySnapshot, isAlive } from "mobx-state-tree";
import { BaseEntity, isNewlyCreated, DictionaryItem } from "modules/common/models/entity";
import { Transport } from "modules/common/models/transport";
import { Notificator } from "modules/common/models/notificator";
import { apiUrls } from "modules/common/services/communication/urls";
import { flow } from "modules/common/models/flow";
import { EMPTY_OBJECT_ID, DATE_TIME_FORMAT } from "modules/common/constants";
import Schema from "../components/details/validation";
import moment from "moment";
import { texts } from "modules/common/texts";
import { nameof } from "modules/common/services/typescript";
import { getFieldLabel } from "modules/common/services/form/fields";

export const IpdTypeDictionaryItem = types
    .compose(
        DictionaryItem,
        types.model({
            description: types.string,
        })
    )
    .named("IpdTypeDictionaryItem");

const IpdTypeBase = types.compose(
    Transport,
    Notificator,
    BaseEntity,
    types.model({
        name: types.string,
        description: types.string,
    })
);

export const IpdType = IpdTypeBase.actions((self) => ({
    load: flow(function* (id: string) {
        try {
            const snapshot = isNewlyCreated(id)
                ? initialState()
                : yield self.transport.get<IpdTypeSnapshotType>(apiUrls.ipdTypes.details(id));

            applySnapshot(self, snapshot);
        } catch (er) {
            self.notify.error(er);
        }
    }),

    save: flow(function* (model: IpdTypeSnapshotType) {
        try {
            const snapshot = self.isNewlyCreated
                ? yield self.transport.put<IpdTypeSnapshotType>(apiUrls.ipdTypes.create(), model)
                : yield self.transport.post<IpdTypeSnapshotType>(apiUrls.ipdTypes.update(self.id), model);

            isAlive(self) && applySnapshot(self, snapshot);
            self.notify.success(texts.messages.saved);

            return true;
        } catch (er) {
            self.notify.error(er);

            return false;
        }
    }),

    delete: flow(function* () {
        if (self.isNewlyCreated) {
            return true;
        }

        try {
            yield self.transport.delete<boolean>(apiUrls.ipdTypes.delete(self.id));
            self.notify.success(texts.messages.removed);

            isAlive(self) && applySnapshot(self, initialState());
            return true;
        } catch (er) {
            self.notify.error(er);
            return false;
        }
    }),
})).named("IpdType");

export type IpdTypeSnapshotType = typeof IpdTypeBase.SnapshotType;
export type IpdTypeType = typeof IpdType.Type;
export type IpdTypeDictionaryItemType = typeof IpdTypeDictionaryItem.Type;

export const initialState = (): IpdTypeSnapshotType => ({
    id: EMPTY_OBJECT_ID,
    name: "",
    created: moment().format(DATE_TIME_FORMAT),
    description: "",
});

export const fields = {
    name: nameof((a: IpdTypeType) => a.name) as string,
    description: nameof((a: IpdTypeType) => a.description) as string,
};

export function formatIpdType(type: IpdTypeSnapshotType | null) {
    let result = "";

    if (type) {
        const schema = Schema();

        result += `${getFieldLabel(fields.name, schema, null)}: ${type.name}\n`;
        result += `${getFieldLabel(fields.description, schema, null)}: ${type.description}\n`;
    }

    return result;
}
